import type {
  ColDef,
  GetQuickFilterTextParams,
  ITooltipParams,
  ICellRendererParams,
  ValueGetterParams,
} from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { IBadgeCellRendererParams } from '~/types/badge'
import type { IRole } from '~/types/role'

import BadgeCellRenderer from '~/components/CellRenderers/BadgeCellRenderer'
import Team from '~/components/Team'
import { rolesComparator, textComparator } from '~/utils/comparators'
import { badgeColDef, lastUpdateColDef } from '~/utils/table'

const RoleCellRenderer = ({ data }: ICellRendererParams): ReactElement => {
  const { description, name } = data as IRole

  return (
    <div className="role-cell">
      <div className="role-cell__name text">{name}</div>
      <div className="role-cell__description text">{description}</div>
    </div>
  )
}

const SpecificOwnersCellRenderer = ({ value }: ICellRendererParams): ReactElement => {
  const users = value as IRole['users']

  if (!users) {
    return <div className="badges-container">-</div>
  }

  return <Team size="small" users={users} />
}

const columns: ColDef[] = [
  {
    cellRenderer: RoleCellRenderer,
    comparator: rolesComparator,
    field: 'identity',
    filter: 'agTextColumnFilter',
    filterParams: {
      buttons: ['clear'],
      debounceMs: 1000,
      textCustomComparator: textComparator,
      trimInput: true,
      valueGetter: ({ data }: ValueGetterParams<IRole>): string | undefined => data?.name,
    },
    getQuickFilterText: ({ data }: GetQuickFilterTextParams<IRole>): string => data.name,
    headerName: 'Role',
    menuTabs: ['filterMenuTab'],
    minWidth: 170,
    tooltipComponent: 'customTooltip',
    tooltipValueGetter: ({ data }: ITooltipParams): IRole => data,
    width: 280,
  },
  badgeColDef(BadgeCellRenderer, {
    field: 'authorizations',
    filterValueGetter: ({ data }: ValueGetterParams<IRole>): string[] | undefined =>
      data?.authorizations?.map(({ key }) => key),
    headerName: 'Related authorizations',
    valueGetter: ({ data }: ValueGetterParams<IRole>): IBadgeCellRendererParams | undefined => {
      if (!data) return undefined

      const { authorizations } = data

      return {
        data:
          authorizations?.map(({ key }) => ({
            id: key,
            name: key,
          })) || [],
        type: 'role',
      }
    },
  }),
  badgeColDef(BadgeCellRenderer, {
    field: 'groups',
    filterValueGetter: ({ data }: ValueGetterParams<IRole>) => data?.groups?.map(({ group_name }) => group_name),
    headerName: 'Owner groups',
    valueGetter: ({ data }: ValueGetterParams<IRole>): IBadgeCellRendererParams | undefined => {
      if (!data) return undefined

      const { groups } = data

      return {
        data:
          groups?.map(({ group_name, id }) => ({
            id,
            name: group_name,
          })) || [],
        type: 'group',
      }
    },
  }),
  {
    cellRenderer: SpecificOwnersCellRenderer,
    field: 'users',
    headerName: 'Specific owners',
    minWidth: 180,
    resizable: false,
    sortable: false,
    suppressHeaderMenuButton: true,
  },
  lastUpdateColDef({ field: 'last_update' }),
]

export default columns
